/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Dialog } from '@coursera/cds-core';

import DefineGoalScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/DefineGoalScreen';
import GeneratingScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/GeneratingScreen';
import MessageSentScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/MessageSentScreen';
import MessageStepper from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/MessageStepper';
import PreviewEmailScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/PreviewEmailScreen';
import ReviewAndSendScreen from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/ReviewAndSendScreen';
import type {
  AIMessageDraft,
  AIMessageScreen,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { mockGenerateDraft } from 'bundles/enterprise-admin-messaging-v2/utils/mockGenerate';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  open: boolean;
  initialPrompt: string;
  onClose: () => void;
  /**
   * How long each of the two Generating loading stages runs. Exposed so
   * tests (and Storybook) can short-circuit the timer.
   */
  generatingStageDurationMs?: number;
};

// Mirror Dialog.HeadingGroup's horizontal padding so the stepper aligns with
// the title's left edge instead of receiving the wider Dialog.Content padding.
const stepperSlot = css`
  padding: 0 var(--cds-spacing-300);
`;

// CDS Dialog hard-codes `max-height: 80vh` on `.cds-Dialog-dialog`. Our AI
// Message modal needs a touch more room so the Review-and-send screen fits
// without an internal scrollbar on standard 1080p displays. The css prop on
// `<Dialog>` composes after CDS's internal styles, so a same-specificity
// override wins.
const dialogMaxHeight = css`
  .cds-Dialog-dialog {
    max-height: 85vh;
  }
`;

const EMPTY_DRAFT: AIMessageDraft = {
  prompt: '',
  audienceSummary: '',
  audienceCount: 0,
  subject: '',
  message: '',
  ctaText: '',
  ctaUrl: '',
  autoSendEnabled: false,
  autoSendCadence: '15_days',
};

const titleForScreen = (screen: AIMessageScreen): string => {
  switch (screen) {
    case 'sent':
      return _t('Message sent successfully');
    case 'review':
      return _t('Review and send');
    case 'preview':
      return _t('Preview and send');
    case 'define':
    case 'generating':
    default:
      return _t('Create a message');
  }
};

/**
 * Top-level dialog. Owns the active sub-screen + draft state, and dispatches
 * to the four sub-screens. Reset every time the modal re-opens so a fresh
 * `initialPrompt` always starts from Step 1.
 */
const AIMessageModal = ({ open, initialPrompt, onClose, generatingStageDurationMs }: Props) => {
  const [activeScreen, setActiveScreen] = React.useState<AIMessageScreen>('define');
  const [draft, setDraft] = React.useState<AIMessageDraft>(EMPTY_DRAFT);

  // Reset the modal to a clean Step 1 state whenever it opens. Using
  // `open` as the trigger keeps the previous draft visible while the
  // close animation plays.
  React.useEffect(() => {
    if (open) {
      setActiveScreen('define');
      setDraft({ ...EMPTY_DRAFT, prompt: initialPrompt });
    }
  }, [open, initialPrompt]);

  const updateDraft = React.useCallback((partial: Partial<AIMessageDraft>) => {
    setDraft((previous) => ({ ...previous, ...partial }));
  }, []);

  const handleGenerate = React.useCallback(() => {
    setActiveScreen('generating');
  }, []);

  const handleGenerateComplete = React.useCallback(() => {
    // TODO: replace `mockGenerateDraft` with the real generate-message GraphQL
    // mutation once the backend lands. The orchestrator pattern stays the
    // same; only the data source swaps out.
    setDraft((previous) => ({ ...previous, ...mockGenerateDraft(previous.prompt) }));
    setActiveScreen('review');
  }, []);

  const handleSend = React.useCallback(() => {
    setActiveScreen('sent');
  }, []);

  const handleBackToDefine = React.useCallback(() => {
    setActiveScreen('define');
  }, []);

  const handlePreviewEmail = React.useCallback(() => {
    setActiveScreen('preview');
  }, []);

  const handleBackToReview = React.useCallback(() => {
    setActiveScreen('review');
  }, []);

  // Unmount the dialog entirely when closed so the modal title doesn't leak
  // into the document and the orchestrator state resets fresh on the next
  // open.
  if (!open) {
    return null;
  }

  let body: React.ReactNode = null;
  switch (activeScreen) {
    case 'define':
      body = (
        <DefineGoalScreen
          prompt={draft.prompt}
          onPromptChange={(value) => updateDraft({ prompt: value })}
          onGenerate={handleGenerate}
          onCancel={onClose}
        />
      );
      break;
    case 'generating':
      body = <GeneratingScreen onComplete={handleGenerateComplete} stageDurationMs={generatingStageDurationMs} />;
      break;
    case 'review':
      body = (
        <ReviewAndSendScreen
          draft={draft}
          updateDraft={updateDraft}
          onSend={handleSend}
          onBack={handleBackToDefine}
          onPreviewEmail={handlePreviewEmail}
        />
      );
      break;
    case 'preview':
      body = <PreviewEmailScreen draft={draft} onSend={handleSend} onBack={handleBackToReview} />;
      break;
    case 'sent':
      body = <MessageSentScreen draft={draft} updateDraft={updateDraft} onDone={onClose} />;
      break;
    default:
      body = null;
  }

  return (
    <Dialog open={open} onClose={onClose} width="large" css={dialogMaxHeight} data-testid="ai-message-modal">
      <Dialog.HeadingGroup>{titleForScreen(activeScreen)}</Dialog.HeadingGroup>
      {(activeScreen === 'define' || activeScreen === 'review') && (
        <div css={stepperSlot}>
          <MessageStepper activeStep={activeScreen === 'define' ? 1 : 2} />
        </div>
      )}
      {body}
    </Dialog>
  );
};

export default AIMessageModal;
